/**
 * Per-type device-row mapper for the Devices view (#999 Story 3.5).
 *
 * Turns a raw repeatable-unit configuration into the compact
 * `DEVICE | DETAILS | STATUS` row shape the Devices cards render. This is a
 * PURE mapping layer — no React, no data fetching — so it is trivially
 * testable and cannot drift from what the cards display.
 *
 * DETAILS come STRAIGHT FROM EACH STEP'S TABLE COLUMNS (Chapman: "use the
 * columns from the table and show those values on the second line"). We reuse
 * the very same `*TableColumnsForLayout` + `format*TableCell` helpers the
 * step tables use, dropping only:
 *   - `NAME`  — that is the DEVICE label (first line), and
 *   - `ROOM`  — the location tag is the card itself.
 * Because the column list and cell formatting are shared with the tables, the
 * Devices second line can never diverge from what a step's table shows.
 */

import { normalizeRepeatableUnitStatus } from './groupUnitsByLocationThenStatus';
import {
  formatCoolingSystemTableCell,
  coolingSystemTableColumnsForLayout,
  type CoolingSystemTableColumnKey,
} from '../components/coolingsystems/coolingSystemListUtils';
import {
  formatLargeUpsTableCell,
  largeUpsTableColumnsForLayout,
  type LargeUpsTableColumnKey,
} from '../components/largeups/largeUpsListUtils';
import {
  formatLiquidCoolingCduTableCell,
  liquidCoolingCduTableColumnsForLayout,
  type LiquidCoolingCduTableColumnKey,
} from '../components/liquidcoolingcdus/liquidCoolingCduListUtils';
import {
  formatPowerDistributionTableCell,
  powerDistributionTableColumnsForLayout,
  type PowerDistributionTableColumnKey,
} from '../components/powerdistribution/powerDistributionListUtils';
import {
  formatRackTableCell,
  rackTableColumnsForLayout,
  type RackTableColumnKey,
} from '../components/racks/rackListUtils';
import {
  formatSmallUpsTableCell,
  smallUpsTableColumnsForLayout,
  type SmallUpsTableColumnKey,
} from '../components/smallups/smallUpsListUtils';

import type { RepeatableUnitStatusBucket } from './groupUnitsByLocationThenStatus';
import type { CoolingSystemConfiguration } from '../types/coolingSystem';
import type { LargeUpsConfiguration } from '../types/largeUps';
import type { LiquidCoolingCduConfiguration } from '../types/liquidCoolingCdu';
import type { PowerDistributionConfiguration } from '../types/powerDistribution';
import type { RackConfiguration } from '../types/rack';
import type { SmallUpsConfiguration } from '../types/smallUps';

/** The six repeatable-unit step ids that surface as devices. */
export type DeviceStepId =
  | 'rack-power'
  | 'small-ups'
  | 'large-ups'
  | 'power-distribution'
  | 'cooling-readiness'
  | 'liquid-cooling';

/** Mirrors the step tables' two layouts (#995/#996). */
export type DeviceTableLayout = 'desktop' | 'compact';

/** Structural union of the six repeatable-unit configuration shapes. */
export type DeviceSourceUnit =
  | RackConfiguration
  | SmallUpsConfiguration
  | LargeUpsConfiguration
  | PowerDistributionConfiguration
  | CoolingSystemConfiguration
  | LiquidCoolingCduConfiguration;

/** One row inside a location card's equipment-type group. */
export interface DeviceRow {
  stepId: DeviceStepId;
  id: number;
  /** DEVICE column — the table's NAME cell (falls back to "<Type> N"). */
  deviceLabel: string;
  /** DETAILS column — the table's other column values, empties dropped. */
  detailFields: string[];
  status: RepeatableUnitStatusBucket;
  /** Sort key within a status bucket (mirrors the step lists). */
  displayOrder: number;
}

/** Short singular type labels used only for the "<Type> N" name fallback. */
const DEVICE_TYPE_FALLBACK_LABEL: Record<DeviceStepId, string> = {
  'rack-power': 'Rack',
  'small-ups': 'Small UPS',
  'large-ups': 'Large UPS',
  'power-distribution': 'Power Distribution',
  'cooling-readiness': 'Cooling System',
  'liquid-cooling': 'CDU',
};

/** The empty-cell sentinel every `format*TableCell` returns. */
const EMPTY_CELL = '—';

/** Columns that never belong on the DETAILS line (handled elsewhere). */
const EXCLUDED_DETAIL_COLUMNS = new Set<string>(['NAME', 'ROOM']);

/**
 * Adapter onto a step table's column list + cell formatter. The `unit`/`column`
 * casts are safe because each adapter is only ever invoked for its own step id.
 */
interface DeviceTableAdapter {
  columnsForLayout: (layout: DeviceTableLayout) => readonly string[];
  nameCell: (unit: DeviceSourceUnit) => string;
  formatCell: (unit: DeviceSourceUnit, column: string) => string;
}

const TABLE_ADAPTERS = new Map<DeviceStepId, DeviceTableAdapter>([
  [
    'rack-power',
    {
      columnsForLayout: layout => rackTableColumnsForLayout(layout),
      nameCell: unit => formatRackTableCell(unit as RackConfiguration, 'NAME'),
      formatCell: (unit, column) =>
        formatRackTableCell(
          unit as RackConfiguration,
          column as RackTableColumnKey,
        ),
    },
  ],
  [
    'small-ups',
    {
      columnsForLayout: layout => smallUpsTableColumnsForLayout(layout),
      nameCell: unit =>
        formatSmallUpsTableCell(unit as SmallUpsConfiguration, 'NAME'),
      formatCell: (unit, column) =>
        formatSmallUpsTableCell(
          unit as SmallUpsConfiguration,
          column as SmallUpsTableColumnKey,
        ),
    },
  ],
  [
    'large-ups',
    {
      columnsForLayout: layout => largeUpsTableColumnsForLayout(layout),
      nameCell: unit =>
        formatLargeUpsTableCell(unit as LargeUpsConfiguration, 'NAME'),
      formatCell: (unit, column) =>
        formatLargeUpsTableCell(
          unit as LargeUpsConfiguration,
          column as LargeUpsTableColumnKey,
        ),
    },
  ],
  [
    'power-distribution',
    {
      columnsForLayout: layout =>
        powerDistributionTableColumnsForLayout(layout),
      nameCell: unit =>
        formatPowerDistributionTableCell(
          unit as PowerDistributionConfiguration,
          'NAME',
        ),
      formatCell: (unit, column) =>
        formatPowerDistributionTableCell(
          unit as PowerDistributionConfiguration,
          column as PowerDistributionTableColumnKey,
        ),
    },
  ],
  [
    'cooling-readiness',
    {
      columnsForLayout: layout => coolingSystemTableColumnsForLayout(layout),
      nameCell: unit =>
        formatCoolingSystemTableCell(
          unit as CoolingSystemConfiguration,
          'NAME',
        ),
      formatCell: (unit, column) =>
        formatCoolingSystemTableCell(
          unit as CoolingSystemConfiguration,
          column as CoolingSystemTableColumnKey,
        ),
    },
  ],
  [
    'liquid-cooling',
    {
      columnsForLayout: layout => liquidCoolingCduTableColumnsForLayout(layout),
      nameCell: unit =>
        formatLiquidCoolingCduTableCell(
          unit as LiquidCoolingCduConfiguration,
          'NAME',
        ),
      formatCell: (unit, column) =>
        formatLiquidCoolingCduTableCell(
          unit as LiquidCoolingCduConfiguration,
          column as LiquidCoolingCduTableColumnKey,
        ),
    },
  ],
]);

/**
 * DETAILS teasers for a unit = every table column for `layout` except NAME and
 * ROOM, formatted exactly as the step table formats it, with empty cells
 * dropped. Defaults to the full (desktop) column set.
 */
export function extractDeviceDetailFields(
  stepId: DeviceStepId,
  unit: DeviceSourceUnit,
  layout: DeviceTableLayout = 'desktop',
): string[] {
  const adapter = TABLE_ADAPTERS.get(stepId);
  if (!adapter) {
    return [];
  }
  const fields: string[] = [];
  for (const column of adapter.columnsForLayout(layout)) {
    if (EXCLUDED_DETAIL_COLUMNS.has(column)) {
      continue;
    }
    const value = adapter.formatCell(unit, column);
    if (value && value !== EMPTY_CELL) {
      fields.push(value);
    }
  }
  return fields;
}

function resolveStatus(
  stepId: DeviceStepId,
  unit: DeviceSourceUnit,
): RepeatableUnitStatusBucket {
  // Racks use `rackStatus`; the other five use the generic `status` (#996).
  const raw =
    stepId === 'rack-power'
      ? (unit as RackConfiguration).rackStatus
      : (unit as Exclude<DeviceSourceUnit, RackConfiguration>).status;
  return normalizeRepeatableUnitStatus(raw);
}

/**
 * DEVICE label = the table's NAME cell. Falls back to "<Type> N" (1-based on the
 * unit's position in its own step list) when the NAME cell is the empty
 * sentinel — e.g. a persisted unit with neither a name nor a display order.
 */
function resolveDeviceLabel(
  stepId: DeviceStepId,
  unit: DeviceSourceUnit,
  index: number,
): string {
  const adapter = TABLE_ADAPTERS.get(stepId);
  const nameCell = adapter?.nameCell(unit) ?? EMPTY_CELL;
  if (nameCell && nameCell !== EMPTY_CELL) {
    return nameCell;
  }
  return `${DEVICE_TYPE_FALLBACK_LABEL[stepId]} ${index + 1}`;
}

/**
 * Map one raw repeatable unit to a Devices-view row. `index` is the unit's
 * position within its own step list (used only for the "<Type> N" fallback).
 * `layout` picks the table column set that feeds the DETAILS line (default
 * desktop = the fuller column set). Returns `null` for units without a
 * persisted id (drafts not yet saved).
 */
export function mapUnitToDeviceRow(
  stepId: DeviceStepId,
  unit: DeviceSourceUnit,
  index: number,
  layout: DeviceTableLayout = 'desktop',
): DeviceRow | null {
  if (unit.id == null) {
    return null;
  }
  return {
    stepId,
    id: unit.id,
    deviceLabel: resolveDeviceLabel(stepId, unit, index),
    detailFields: extractDeviceDetailFields(stepId, unit, layout),
    status: resolveStatus(stepId, unit),
    displayOrder: unit.displayOrder ?? 0,
  };
}
